/**
 * ErrorHandler Service
 * Centralized error management with user feedback
 * Singleton pattern for application-wide error handling
 */

import EmailRevealClient from '../clients/EmailRevealClient.js';
import { ERROR, CLOUD_FUNCTIONS } from '../constants/index.js';
import { escapeHTML } from '../utils/common.js';
import { trackError } from '../services/AnalyticsService.js';

class ErrorHandler {
    constructor() {
        this.errorQueue = [];
        this.maxQueueSize = ERROR.MAX_ERROR_QUEUE_SIZE;
        this.listeners = [];
        this.isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    }

    /**
     * Register callback for error events
     * @param {Function} callback - Called with (error, errorInfo) when error occurs
     */
    subscribe(callback) {
        this.listeners.push(callback);
        return () => {
            this.listeners = this.listeners.filter(l => l !== callback);
        };
    }

    /**
     * Notify all listeners of an error
     * @private
     */
    #notifyListeners(error, context) {
        this.listeners.forEach(callback => {
            try {
                callback(error, context);
            } catch (e) {
                console.error('Error in error listener:', e);
            }
        });
    }

    /**
     * Handle an error with optional context
     * @param {Error|string} error - Error object or message
     * @param {Object} context - Additional context (viewSlug, operation, etc.)
     * @param {string} severity - 'info', 'warning', 'error', 'critical'
     */
    handle(error, context = {}, severity = 'error') {
        const errorInfo = {
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : null,
            timestamp: new Date().toISOString(),
            severity,
            context,
            isDev: this.isDev
        };

        // Add to queue
        this.errorQueue.push(errorInfo);
        if (this.errorQueue.length > this.maxQueueSize) {
            this.errorQueue.shift();
        }

        // Log based on severity
        switch (severity) {
            case ERROR.SEVERITY.CRITICAL:
                console.error('🔴 CRITICAL ERROR:', errorInfo);
                break;
            case ERROR.SEVERITY.ERROR:
                console.error('❌ ERROR:', errorInfo);
                break;
            case ERROR.SEVERITY.WARNING:
                console.warn('⚠️ WARNING:', errorInfo);
                break;
            case ERROR.SEVERITY.INFO:
                console.info('ℹ️ INFO:', errorInfo);
                break;
        }

        // Notify listeners (UI components can show toast/banner)
        this.#notifyListeners(error, errorInfo);

        // Track error to Analytics
        trackError(severity, errorInfo.message, context);

        // Optional: Send to Firebase for production error tracking
        if (!this.isDev && severity === ERROR.SEVERITY.CRITICAL) {
            this.#logToFirebase(errorInfo);
        }

        return errorInfo;
    }

    /**
     * Log error to Firebase for production monitoring
     * @private
     */
    async #logToFirebase(errorInfo) {
        try {
            if (window.navigator.onLine) {
                // Could integrate with Firestore error logging here
                // For now, just track that we tried
                // Firebase integration placeholder - could integrate with Firestore error logging
            }
        } catch (e) {
            console.error('Failed to log to Firebase:', e);
        }
    }

    /**
     * Clear error queue
     */
    clearQueue() {
        this.errorQueue = [];
    }

    /**
     * Get error history
     * @returns {Array} Array of recent errors
     */
    getHistory() {
        return [...this.errorQueue];
    }

    /**
     * Handle async operation with automatic error catching
     * @param {Promise} promise - Promise to handle
     * @param {string} operationName - Name of operation for context
     * @returns {Promise} Original promise with error handling attached
     */
    trackAsync(promise, operationName = 'Async Operation') {
        return promise.catch(error => {
            this.handle(error, { operation: operationName }, 'error');
            throw error; // Re-throw for caller to handle if needed
        });
    }

    /**
     * Create a safe async wrapper
     * @param {Function} asyncFn - Async function to wrap
     * @param {string} operationName - Name for error context
     * @returns {Function} Wrapped function
     */
    wrapAsync(asyncFn, operationName = 'Unknown Operation') {
        return async (...args) => {
            try {
                return await asyncFn(...args);
            } catch (error) {
                this.handle(error, { operation: operationName, args: args.length }, 'error');
                throw error;
            }
        };
    }

    /**
     * Display a critical initialization error with UI banner and secure email reveal
     * Uses the same EmailRevealService pattern as the contact page
     * @param {Error|string} error - Error object or message
     * @param {Object} context - Additional context
     * @param {string} functionName - Cloud Function name (e.g., 'getAdminEmail'). Defaults to CLOUD_FUNCTIONS.DEFAULT_ERROR_FUNCTION
     */
    displayCriticalError(error, context = {}, functionName = CLOUD_FUNCTIONS.DEFAULT_ERROR_FUNCTION) {
        // Log the error
        this.handle(error, context, 'critical');
        
        // Display error banner in UI with secure email reveal button
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
            const buttonId = 'error-contact-btn-' + Date.now();
            mainContent.innerHTML = `
                <div class="p-8 bg-red-50 border-2 border-red-300 rounded-lg max-w-md mx-auto">
                    <h2 class="text-red-700 font-bold text-lg mb-2">Initialization Error</h2>
                    <p class="text-red-600 mb-4">The application failed to start. Please refresh the page or contact support.</p>
                    <div class="flex gap-3">
                        <button onclick="location.reload()" class="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 cursor-pointer font-medium">
                            Refresh Page
                        </button>
                        <button id="${buttonId}" class="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 cursor-pointer font-medium">
                            Show Email
                        </button>
                    </div>
                </div>
            `;
            
            // Set up secure email reveal using the same EmailRevealService pattern as contact page
            try {
                const cloudFunctionUrl = this.#getCloudFunctionUrl(functionName);
                const emailRevealService = new EmailRevealClient(() => 'Show Email');
                emailRevealService.setupEmailRevealButton(buttonId, cloudFunctionUrl, 'error', 'showEmail', 'loadingEmail');
            } catch (e) {
                // If Cloud Function not configured, just show static email
                console.warn('Cloud Function not available for error display:', e.message);
            }
        }
    }

    /**
     * Get Cloud Function URL with fallback to environment configuration
     * Mirrors AppViewModel's approach for consistency
     * @private
     */
    #getCloudFunctionUrl(functionName) {
        // Try environment variable first (most secure approach)
        const envKey = `VITE_CLOUD_FUNCTION_${functionName.toUpperCase()}_URL`;
        if (import.meta.env[envKey]) {
            return import.meta.env[envKey];
        }
        
        // For backwards compatibility, try to get projectId and region from env
        const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
        const region = import.meta.env.VITE_CLOUD_FUNCTION_REGION || 'us-central1';
        
        if (!projectId) {
            throw new Error(`Cloud Function URL not configured for ${functionName}. Set ${envKey} or VITE_FIREBASE_PROJECT_ID.`);
        }
        
        // Reconstruct URL from projectId with configurable region (fallback)
        return `https://${region}-${projectId}.cloudfunctions.net/${functionName}`;
    }



    /**
     * Display error in a user-friendly popup modal
     * Leverages AppViewModel's modal system for consistent UI
     * @param {string} message - Error message to display
     * @param {string} title - Modal title (default: 'Error')
     * @param {string} severity - Visual style: 'info', 'warning', 'error', 'critical'
     */
    showErrorPopup(message, title = 'Error', severity = 'error') {
        try {
            const appVM = window.appViewModel;
            if (!appVM || !appVM.showModal) {
                // Fallback: if modal unavailable, log and show alert
                console.error('ErrorHandler: Modal unavailable, using alert()', message);
                alert(`${title}\n\n${message}`);
                return;
            }

            // Map severity to color scheme
            const colorMap = {
                info: 'bg-blue-50 border-blue-300 text-blue-700',
                warning: 'bg-yellow-50 border-yellow-300 text-yellow-700',
                error: 'bg-red-50 border-red-300 text-red-700',
                critical: 'bg-red-100 border-red-400 text-red-800'
            };
            const colors = colorMap[severity] || colorMap.error;

            // Build modal HTML
            const modalHtml = `
                <div class="p-6 ${colors} border-2 rounded-lg max-w-md">
                    <h2 class="font-bold text-lg mb-2">${escapeHTML(title)}</h2>
                    <p class="mb-4">${escapeHTML(message)}</p>
                    <button onclick="window.appViewModel.closeModal()" class="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 cursor-pointer font-medium">
                        OK
                    </button>
                </div>
            `;

            appVM.showModal(modalHtml);

            // Still log to console for debugging
            console.error(`[${severity.toUpperCase()}] ${title}: ${message}`);
        } catch (e) {
            console.error('ErrorHandler.showErrorPopup() failed:', e);
            alert(`${title}\n\n${message}`);
        }
    }

    /**
     * Create error recovery strategy
     * @param {Promise} promise - Promise to attempt
     * @param {Function} fallback - Fallback function if promise fails
     * @param {string} operationName - Name for logging
     * @returns {Promise}
     */
    async withFallback(promise, fallback, operationName = 'Operation') {
        try {
            return await promise;
        } catch (error) {
            this.handle(error, { operation: operationName, recoveryAttempted: true }, 'warning');
            try {
                return await fallback();
            } catch (fallbackError) {
                this.handle(fallbackError, { operation: `${operationName} (fallback)` }, 'error');
                throw fallbackError;
            }
        }
    }
}

// Create singleton instance
const errorHandler = new ErrorHandler();

export default errorHandler;
